import axiosClient from './axiosClient';
import type { RfoPhase, WorkflowStatusResponse, WorkflowTransition } from '@/types/workflow';

export const workflowApi = {
  getWorkflowStatus: async (opportunityId: string): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.get(`/opportunities/${opportunityId}/workflow`);
    return response.data;
  },

  initializeWorkflow: async (opportunityId: string): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/initialize`);
    return response.data;
  },

  advanceWorkflow: async (opportunityId: string, reason: string): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/advance`, { reason });
    return response.data;
  },

  canAdvance: async (opportunityId: string): Promise<{ canAdvance: boolean }> => {
    const response = await axiosClient.get(`/opportunities/${opportunityId}/workflow/can-advance`);
    return response.data;
  },

  getTransitions: async (opportunityId: string): Promise<WorkflowTransition[]> => {
    const response = await axiosClient.get(`/opportunities/${opportunityId}/workflow/transitions`);
    return response.data;
  },

  holdWorkflow: async (opportunityId: string): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/hold`);
    return response.data;
  },

  cancelWorkflow: async (opportunityId: string): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/cancel`);
    return response.data;
  },

  rollbackWorkflow: async (
    opportunityId: string,
    targetPhase: RfoPhase,
    reason: string,
  ): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/rollback`, {
      targetPhase,
      reason,
    });
    return response.data;
  },

  moveToPhase: async (
    opportunityId: string,
    targetPhase: RfoPhase,
    reason: string,
    assignToUserId?: string,
  ): Promise<WorkflowStatusResponse> => {
    const response = await axiosClient.post(`/opportunities/${opportunityId}/workflow/move-to-phase`, {
      targetPhase,
      reason,
      assignToUserId,
    });
    return response.data;
  },

  notifyPhaseEdit: async (opportunityId: string, editedPhase: RfoPhase): Promise<void> => {
    await axiosClient.post(`/opportunities/${opportunityId}/workflow/notify-phase-edit`, {
      editedPhase,
    });
  },

  changePhaseOwner: async (
    opportunityId: string,
    phase: RfoPhase,
    newOwnerId: string,
    backupOwnerId?: string,
  ): Promise<WorkflowStatusResponse> => {
    const params: Record<string, string> = { newOwnerId };
    if (backupOwnerId) params.backupOwnerId = backupOwnerId;
    const response = await axiosClient.put(
      `/opportunities/${opportunityId}/workflow/phases/${phase}/owner`,
      null,
      { params },
    );
    return response.data;
  },
};
